import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Bug } from 'lucide-react';
import { AuthContext } from '../App';
import TesterReportModal from './common/TesterReportModal';

// UnityLove Support Phone
const UNITYLOVE_SUPPORT_PHONE_DISPLAY = "(866) 260-6427";
const UNITYLOVE_SUPPORT_PHONE_TEL = "+18662606427";

/**
 * Global Footer Component
 * Appears on all pages (logged in and logged out)
 * Contains: Platform links, Support/Help, Legal pages, Tester Report button
 */
const GlobalFooter = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const [reportOpen, setReportOpen] = useState(false);
  
  // Feature flag for "What's forming" link
  const showComingSoon = process.env.REACT_APP_SHOW_COMING_SOON === "1";

  // Determine if report button should show
  const showReportButton = useMemo(() => {
    if (process.env.REACT_APP_TESTER_REPORTS !== "1") return false;
    if (!user?.email) return false; // only for logged-in users

    const raw = (process.env.REACT_APP_TESTER_EMAIL_ALLOWLIST || "").trim();
    if (!raw) return true; // if no allowlist is set, show to all logged-in users (beta mode)

    const allow = raw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    return allow.includes(String(user.email).toLowerCase());
  }, [user]);

  const handleNavigation = (e, path) => {
    e.preventDefault();
    // Handle hash links (for landing page sections)
    if (path.startsWith('#')) {
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        // If section doesn't exist, go to landing page
        navigate(`/${path}`);
      }
    } else {
      navigate(path);
    }
  };

  return (
    <footer 
      className="bg-gray-900 text-white py-12 mt-auto"
      style={{ paddingBottom: 'calc(2rem + var(--bottom-safe-space, 80px))' }}
    >
      {/* Uses --bottom-safe-space for PWA banner + Emergent watermark clearance */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Heart className="w-6 h-6 text-rose-400 fill-current" />
              <span className="font-playfair text-xl font-bold">Unity Love</span>
            </div>
            <p className="text-gray-400 text-sm">
              Connecting hearts, celebrating culture, building futures.
            </p>
          </div>
          
          {/* Platform Links */}
          <div>
            <h4 className="font-semibold mb-4">Platform</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              {user && (
                <li>
                  <a
                    href="/daily-mirror"
                    onClick={(e) => handleNavigation(e, '/daily-mirror')}
                    className="hover:text-white transition-colors"
                  >
                    Daily Mirror
                  </a>
                </li>
              )}
              <li>
                <a 
                  href="/how-it-works" 
                  onClick={(e) => handleNavigation(e, '/how-it-works')}
                  className="hover:text-white transition-colors"
                >
                  How it works
                </a>
              </li>
              <li>
                <a 
                  href="/#community" 
                  onClick={(e) => handleNavigation(e, '#community')}
                  className="hover:text-white transition-colors"
                >
                  Success stories
                </a>
              </li>
              <li>
                <a 
                  href="/safety" 
                  onClick={(e) => handleNavigation(e, '/safety')}
                  className="hover:text-white transition-colors"
                >
                  Safety
                </a>
              </li>
              <li>
                <a 
                  href="/communities" 
                  onClick={(e) => handleNavigation(e, '/communities')}
                  className="hover:text-white transition-colors"
                >
                  Communities & Regions
                </a>
              </li>
              <li>
                <a 
                  href="/initiatives" 
                  onClick={(e) => handleNavigation(e, '/initiatives')}
                  className="text-gray-500 hover:text-white/80 transition-colors"
                >
                  Initiatives
                </a>
              </li>
            </ul>
          </div>
          
          {/* Support Links */}
          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <a 
                  href="/help" 
                  onClick={(e) => handleNavigation(e, '/help')}
                  className="hover:text-white transition-colors"
                >
                  Help center
                </a>
              </li>
              <li>
                <a 
                  href="/contact"
                  onClick={(e) => handleNavigation(e, '/contact')}
                  className="hover:text-white transition-colors"
                >
                  Contact us
                </a>
              </li>
              <li>
                <a 
                  href="/guidelines" 
                  onClick={(e) => handleNavigation(e, '/guidelines')}
                  className="hover:text-white transition-colors"
                >
                  Guidelines
                </a>
              </li>
              <li>
                <a 
                  href="/guides" 
                  onClick={(e) => handleNavigation(e, '/guides')}
                  className="hover:text-white transition-colors"
                  data-testid="footer-guides-link"
                >
                  Guides & Resources
                </a>
              </li>
              <li>
                <a 
                  href="/reports" 
                  onClick={(e) => handleNavigation(e, '/reports')}
                  className="hover:text-white transition-colors"
                  data-testid="footer-reports-link"
                >
                  Research & Reports
                </a>
              </li>
              <li>
                <a 
                  href="/education/love-your-way" 
                  onClick={(e) => handleNavigation(e, '/education/love-your-way')}
                  className="hover:text-rose-400 transition-colors font-medium"
                >
                  💜 Love Your Way Guide
                </a>
              </li>
            </ul>
          </div>
          
          {/* Legal Links */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <a 
                  href="/terms" 
                  onClick={(e) => handleNavigation(e, '/terms')}
                  className="hover:text-white transition-colors"
                >
                  Terms of service
                </a>
              </li>
              <li>
                <a 
                  href="/privacy" 
                  onClick={(e) => handleNavigation(e, '/privacy')}
                  className="hover:text-white transition-colors"
                >
                  Privacy policy
                </a>
              </li>
              <li>
                <a 
                  href="/privacy" 
                  onClick={(e) => handleNavigation(e, '/privacy')}
                  className="hover:text-white transition-colors"
                >
                  Cookie policy
                </a>
              </li>
              {/* "What's forming" - Only show if feature flag enabled */}
              {showComingSoon && (
                <li>
                  <a 
                    href="/coming-soon" 
                    onClick={(e) => handleNavigation(e, '/coming-soon')}
                    className="hover:text-rose-300 transition-colors text-gray-500"
                    title="A quiet look at what we're building"
                  >
                    What&apos;s forming
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; 2025 Unity Love. All rights reserved.</p>
          
          {/* Support Phone */}
          <div className="mt-4 text-sm text-gray-400">
            <span className="mr-2">Support:</span>
            <a
              href={`tel:${UNITYLOVE_SUPPORT_PHONE_TEL}`}
              className="text-gray-200 hover:text-white hover:underline"
              aria-label={`Call UnityLove at ${UNITYLOVE_SUPPORT_PHONE_DISPLAY}`}
            >
              {UNITYLOVE_SUPPORT_PHONE_DISPLAY}
            </a>
          </div>
          
          {/* Secondary link for mobile - only show if feature flag enabled */}
          {showComingSoon && (
            <a 
              href="/coming-soon" 
              onClick={(e) => handleNavigation(e, '/coming-soon')}
              className="inline-block mt-3 text-gray-600 hover:text-rose-300 transition-colors text-xs"
              title="A quiet look at what we're building"
            >
              What&apos;s forming →
            </a>
          )}
          
          {/* Tester Report Button */}
          {showReportButton && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-rose-400 transition-colors"
              >
                <Bug className="w-3 h-3" />
                Report a problem
              </button>
            </div>
          )}
        </div>
        
        {/* Tester Report Modal */}
        <TesterReportModal 
          open={reportOpen} 
          onClose={() => setReportOpen(false)} 
          currentUser={user} 
        />
      </div>
    </footer>
  );
};

export default GlobalFooter;
